import {
  AfterContentChecked,
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
let nextId = 0;

@Directive({
    selector: 'ng-template[libGungTabTitle]',
    standalone: false
})
export class GungTabTitleDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

@Directive({
    selector: 'ng-template[libGungTabContent]',
    standalone: false
})
export class GungTabContentDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({
    selector: 'lib-gung-tab',
    standalone: false
})
export class GungTabDirective implements AfterContentChecked {
  @Input() id = `gung-tab-${nextId++}`;

  @Input() title: string;

  @Input() icon?: string;

  /** Groups tabs in a collapsible vertical sidebar: a heading is shown where the section changes. */
  @Input() section?: string;

  /** Makes the sidebar entry a router link instead of a tab; it has no content and is never the active tab. */
  @Input() link?: string | any[];

  @Input() disabled = false;

  titleTpl: GungTabTitleDirective | null;
  contentTpl: GungTabContentDirective | null;

  @ContentChildren(GungTabTitleDirective, { descendants: false }) titleTpls: QueryList<GungTabTitleDirective>;
  @ContentChildren(GungTabContentDirective, { descendants: false }) contentTpls: QueryList<GungTabContentDirective>;

  ngAfterContentChecked() {
    // We are using @ContentChildren instead of @ContentChild as in the Angular version being used
    // only @ContentChildren allows us to specify the {descendants: false} option.
    // Without {descendants: false} we are hitting bugs described in:
    // https://github.com/ng-bootstrap/ng-bootstrap/issues/2240
    this.titleTpl = this.titleTpls.first;
    this.contentTpl = this.contentTpls.first;
  }
}

export interface GungTabChangeEvent {
  activeId: string;

  nextId: string;

  preventDefault: () => void;
}

@Component({
    selector: 'lib-gung-tabset',
    exportAs: 'gungTabset',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './gung-tabs.component.html',
    styleUrls: ['./gung-tabs.component.scss'],
    standalone: false
})
export class GungTabsetComponent implements AfterContentChecked {
  justifyClass: string;

  activeTabTitle;

  showMobileMenu = false;

  sidebarCollapsed = false;

  // {descendants: false} avoids picking up tabs from a nested lib-gung-tabset (e.g. a grid's
  // editor panel rendered inside a container's editor panel) - see ng-bootstrap issue #2240.
  @ContentChildren(GungTabDirective, { descendants: false }) tabs: QueryList<GungTabDirective>;

  /**
   * The identifier of the tab that should be opened **initially**.
   *
   * For subsequent tab switches use the `.select()` method and the `(tabChange)` event.
   */
  // @Input() activeId: string;
  _activeId: string;
  @Input()
  set activeId(val: string) {
    this.activeIdChange.emit(val);
    this._activeId = val;
  }
  get activeId() {
    return this._activeId;
  }
  @Output()
  activeIdChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * If `true`, non-visible tabs content will be removed from DOM. Otherwise it will just be hidden.
   */
  @Input() destroyOnHide = true;

  /**
   * The horizontal alignment of the tabs with flexbox utilities.
   */
  @Input()
  set justify(className: 'start' | 'center' | 'end' | 'fill' | 'justified') {
    if (className === 'fill' || className === 'justified') {
      this.justifyClass = `nav-${className}`;
    } else {
      this.justifyClass = `justify-content-${className}`;
    }
  }

  /**
   * The orientation of the tabset.
   */
  @Input() orientation: 'horizontal' | 'vertical';

  /**
   * If `true`, the vertical sidebar will show a collapse toggle button and support an icon-rail collapsed state.
   * Requires `orientation="vertical"`.
   */
  @Input() collapsible = false;

  /**
   * Label shown at the top of the collapsible sidebar (e.g. "CUSTOMER").
   */
  @Input() sidebarLabel?: string;

  /**
   * A tab change event emitted right before the tab change happens.
   *
   */
  @Output() tabChange = new EventEmitter<GungTabChangeEvent>();

  constructor() {
    this.justify = 'start';
    this.orientation = 'horizontal';
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  get contentTabs(): GungTabDirective[] {
    return this.tabs ? this.tabs.filter(tab => !tab.link) : [];
  }

  startsSection(index: number): boolean {
    const tab = this.tabs.get(index);
    return !!tab?.section && (index === 0 || this.tabs.get(index - 1)?.section !== tab.section);
  }

  /**
   * Selects the tab with the given id and shows its associated content panel.
   *
   * Any other tab that was previously selected becomes unselected and its associated pane is removed from DOM or
   * hidden depending on the `destroyOnHide` value.
   */
  onNavChange(event: { activeId: string; nextId: string; preventDefault: () => void }): void {
    const selectedTab = this._getTabById(event.nextId);
    if (selectedTab && !selectedTab.disabled && this.activeId !== selectedTab.id) {
      let defaultPrevented = false;

      this.tabChange.emit({
        activeId: this.activeId,
        nextId: selectedTab.id,
        preventDefault: () => {
          defaultPrevented = true;
          event.preventDefault();
        }
      });

      if (!defaultPrevented) {
        this.activeId = selectedTab.id;
      }
    }
    if (this.orientation === 'vertical') {
      this.showMobileMenu = false;
    }
  }

  select(tabId: string) {
    const selectedTab = this._getTabById(tabId);
    if (selectedTab && !selectedTab.disabled && this.activeId !== selectedTab.id) {
      this.activeId = selectedTab.id;
    }
    if (this.orientation === 'vertical' || this.collapsible) {
      this.showMobileMenu = false;
    }
  }

  ngAfterContentChecked() {
    // auto-correct activeId that might have been set incorrectly as input
    const activeTab = this._getTabById(this.activeId);
    const firstContentTab = this.tabs.find(tab => !tab.link);
    this.activeId = activeTab && !activeTab.link ? activeTab.id : firstContentTab ? firstContentTab.id : (null as any);
    if(this.orientation==='vertical'){
      this.setActiveTabTitle();
    }
    
  }

  private _getTabById(id: string): GungTabDirective {
    const tabsWithId: GungTabDirective[] = this.tabs.filter(tab => tab.id === id);
    return tabsWithId.length ? tabsWithId[0] : (null as any);
  }

  setActiveTabTitle() {
    const activeTab = this.tabs.find(tab => tab.id === this.activeId);
    if (activeTab) {
      this.activeTabTitle = {};
      this.activeTabTitle.isTemplate = activeTab.titleTpl?.templateRef ? true : false;
      this.activeTabTitle.content = activeTab.titleTpl?.templateRef ? activeTab.titleTpl?.templateRef: activeTab.title ;
    }
  }
}
